/**
 * Service for Lotto API calls
 * Communicates with backend REST endpoints
 */

import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../config/properties';

// Error handling utility
const handleApiError = (error, context = '') => {
  console.error(`Lotto Service Error (${context}):`, error);

  if (error instanceof SyntaxError) {
    throw new Error('Invalid response format from server. Response was not valid JSON.');
  }

  if (error.message && error.message.includes('Failed to fetch')) {
    throw new Error('Network error. Please check your connection and that the API server is running.');
  }

  throw error;
};

const lottoService = {
  /**
   * Get all Lotto wheels
   */
  getAllWheels: async () => {
    try {
      console.log('Fetching lotto wheels from:', API_ENDPOINTS.lottoWheels);
      const response = await httpClient.get(API_ENDPOINTS.lottoWheels);
      const data = await response.json();
      console.log('Wheels fetched successfully:', data);
      return data;
    } catch (error) {
      handleApiError(error, 'getAllWheels');
    }
  },

  /**
   * Get statistics for a specific wheel
   */
  getWheelStatistics: async (wheelCode, topN = 10) => {
    try {
      const url = `${API_ENDPOINTS.lottoWheelStatistics}/${wheelCode}/statistics?topN=${topN}`;
      console.log('Fetching wheel statistics from:', url);
      const response = await httpClient.get(url);
      const data = await response.json();
      console.log('Statistics fetched successfully:', data);
      return data;
    } catch (error) {
      handleApiError(error, 'getWheelStatistics');
    }
  },

  /**
   * Get latest extractions for a wheel
   */
  getLatestExtractions: async (wheelCode, limit = 10) => {
    try {
      const url = `${API_ENDPOINTS.lottoLatest}/${wheelCode}/latest?limit=${limit}`;
      console.log('Fetching latest extractions from:', url);
      const response = await httpClient.get(url);
      const data = await response.json();
      console.log('Latest extractions fetched successfully:', data);
      return data;
    } catch (error) {
      handleApiError(error, 'getLatestExtractions');
    }
  },

  /**
   * Get extractions by date range for a wheel
   */
  getExtractionsByDateRange: async (wheelCode, fromDate, toDate) => {
    try {
      const url = `${API_ENDPOINTS.lottoWheels}/${wheelCode}/by-date-range?from=${fromDate}&to=${toDate}`;
      console.log('Fetching extractions by date range from:', url);
      const response = await httpClient.get(url);
      const data = await response.json();
      console.log('Extractions by date range fetched successfully:', data);
      return data;
    } catch (error) {
      handleApiError(error, 'getExtractionsByDateRange');
    }
  },

  /**
   * Get extractions by date for all wheels
   */
  getExtractionsByDate: async (date) => {
    try {
      const url = `${API_ENDPOINTS.lottoWheels}/by-date?date=${date}`;
      console.log('Fetching extractions by date from:', url);
      const response = await httpClient.get(url);
      const data = await response.json();
      console.log('Extractions by date fetched successfully:', data);
      return data;
    } catch (error) {
      handleApiError(error, 'getExtractionsByDate');
    }
  },

  /**
   * Get latest extraction from all wheels
   */
  getLatestFromAllWheels: async () => {
    try {
      const url = `${API_ENDPOINTS.lottoWheels}/all/latest`;
      console.log('Fetching latest from all wheels from:', url);
      const response = await httpClient.get(url);
      const data = await response.json();
      console.log('Latest from all wheels fetched successfully:', data);
      return data;
    } catch (error) {
      handleApiError(error, 'getLatestFromAllWheels');
    }
  },

  /**
   * Get delay numbers (statistics) for a wheel
   */
  getDelayNumbers: async (wheelCode, sortBy = 'frequency') => {
    try {
      const url = `${API_ENDPOINTS.lottoDelayNumbers}/${wheelCode}/delay-numbers?sortBy=${sortBy}`;
      console.log('Fetching delay numbers from:', url);
      const response = await httpClient.get(url);
      const data = await response.json();
      console.log('Delay numbers fetched successfully:', data);
      return data;
    } catch (error) {
      handleApiError(error, 'getDelayNumbers');
    }
  },

  /**
   * Health check
   */
  health: async () => {
    try {
      console.log('Fetching lotto health from:', API_ENDPOINTS.lottoHealth);
      const response = await httpClient.get(API_ENDPOINTS.lottoHealth);
      const data = await response.json();
      console.log('Health check successful:', data);
      return data;
    } catch (error) {
      handleApiError(error, 'health');
    }
  },
};

export default lottoService;
